import { GoDotFill } from '@react-icons/all-files/go/GoDotFill'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import { Input } from '~/components/common/CurieInput'
import { isSuccessTerminated, TaskStatusDetailType, TaskStatuses } from '~/constants/task-status'
import { useTask } from '~/contexts/TaskContext'

/** Gray spinner that matches the timeline's dot aesthetic */
function TimelineSpinner() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      className='animate-spin'
    >
      <circle cx='12' cy='12' r='9' stroke='#E8ECEF' strokeWidth='3' />
      <path
        d='M12 3C16.9706 3 21 7.02944 21 12'
        stroke='#A0AAB4'
        strokeWidth='3'
        strokeLinecap='round'
      />
    </svg>
  )
}

function TimelineStatusIcon({ isActive }: { isActive: boolean }) {
  if (isActive) {
    return (
      <div className='absolute top-[4px] left-[4px] w-6 h-6 flex items-center justify-center'>
        <TimelineSpinner />
      </div>
    )
  }
  return <GoDotFill className='absolute top-0' color='#D0D7DE' size={32} />
}
import { useConversationUuid } from '~/hooks'
import { ModalId, useModalController } from '~/hooks/useModal'
import { useTaskAwareMetadata } from '~/hooks/useTaskAware'
import {
  AgentTaskStatus,
  agentTaskStatusFromJSON,
  CommandType,
  commandTypeFromJSON
} from '~/protos/agents'
import { regenerateImage } from '~/services/taskService'
import { cm, isCampaignUuid, verifyUUID } from '~/utils/utils'
import { Block as BlockType } from '../chat/message'
import Planner from '../code-plan/Planner'
import Document from '../editors/lexical/Document'
import { TaskStatusDetailModalProps } from '../modals/TaskStatusDetailsModal'
import { UserPopover } from '../account/UserPopover'
import { Block } from './Block'
import styles from './TimelineBlock.module.scss'

export interface TimelineBlockProps {
  totalBlock: number
  children: BlockType[]
  fields: {
    ts: number
    desc: string
    status: string
    index: number
    user_id: number
    user_name: string
  }
  _uuid: string
  task?: unknown // Add task prop
}

export function TimelineBlock({ totalBlock, fields, children, _uuid }: TimelineBlockProps) {
  const { ts, desc, status, index, user_id, user_name } = fields
  const renderDate = (date: number | string) => {
    if (typeof date === 'string') return date
    if (typeof date === 'number') {
      return dayjs(date).format('h:mm:ss A, MMM DD, YYYY')
    }
  }
  const taskStatus = agentTaskStatusFromJSON(status)
  const { task } = useTask()
  const currentTaskStatus = task ? agentTaskStatusFromJSON(task.status) : null

  // Memoize the current tuple to avoid repeated lookups
  const currentTuple = useMemo(() => {
    if (!task?.details?.orderedStateResponses) return null
    return task.details.orderedStateResponses[index] || null
  }, [task?.details?.orderedStateResponses, index])

  // Get agent internal status message if available (memoized)
  const internalMessage = useMemo(() => {
    if (!currentTuple) return null
    const internalStatus = currentTuple.response?.[0]?.responseContentStream?.agentInternalStatus
    if (internalStatus?.internalProgressMsg && internalStatus.internalProgressMsg.length > 0) {
      return internalStatus.internalProgressMsg[internalStatus.internalProgressMsg.length - 1]
    }
    return null
  }, [currentTuple])

  const hasTaskDetail = TaskStatuses[taskStatus].detailType !== TaskStatusDetailType.NONE
  const hasPlan = taskStatus == AgentTaskStatus.PLANNING_COMPLETED
  const documentGenerated = taskStatus == AgentTaskStatus.DOCUMENT_GENERATED
  const imageGenerated = taskStatus == AgentTaskStatus.IMAGE_GENERATED

  const { openModal } = useModalController<TaskStatusDetailModalProps>(ModalId.TaskStatusDetails)
  const { openModal: openRefactorFileViewerModal } = useModalController(ModalId.RefactorFileViewer)
  const { openModal: openImageViewerModal } = useModalController(ModalId.ImageViewer)
  const url = useConversationUuid()
  const isAdminTask = !verifyUUID(url)
  const { workspaceUuid, uuid } = useParams<{ workspaceUuid: string; uuid: string }>()
  const isCampaign = isCampaignUuid(uuid)

  const onClickDocs = () => {
    if (task && currentTuple && docDiffs.length > 0) {
      openRefactorFileViewerModal({
        title: fields.desc,
        index,
        uuid,
        diffs: docDiffs,
        readOnly: true,
        modalId: ModalId.RefactorFileViewer,
        currentTaskStatus,
        modalTitle: 'Review API Documentation'
      })
    }
  }

  const onClickDiagram = () => {
    if (imageDiffs.length > 0) {
      openImageViewerModal({
        title: 'API Diagrams',
        imageDiffs,
        modalId: ModalId.ImageViewer
      })
    }
  }

  const onClickDetails = (clickedTaskStatus: AgentTaskStatus) => {
    // Handle refactor states with unified modal
    if (
      clickedTaskStatus === AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED ||
      clickedTaskStatus === AgentTaskStatus.REFACTOR_API_SPECS_GENERATED ||
      clickedTaskStatus === AgentTaskStatus.REFACTOR_CODE_GENERATED
    ) {
      if (task && currentTuple) {
        // Determine readOnly status
        let isReadOnly = true
        let modalTitle = 'Review Generated Documentation'

        if (clickedTaskStatus === AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED) {
          isReadOnly = !(
            clickedTaskStatus === AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED &&
            currentTaskStatus === AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED
          )
          modalTitle = 'Review Generated Documentation'
        } else if (clickedTaskStatus === AgentTaskStatus.REFACTOR_API_SPECS_GENERATED) {
          isReadOnly = true
          modalTitle = 'Review API Documentation'
        } else if (clickedTaskStatus === AgentTaskStatus.REFACTOR_CODE_GENERATED) {
          isReadOnly = true
          modalTitle = 'Review Generated Code'
        }

        openRefactorFileViewerModal({
          title: fields.desc,
          index,
          uuid,
          diffs,
          readOnly: isReadOnly,
          modalId: ModalId.RefactorFileViewer,
          currentTaskStatus,
          modalTitle
        })
        return
      }
    }

    // Fall back to default modal for other states
    openModal({ title: fields.desc, taskStatus: clickedTaskStatus, index })
  }

  const renderUser = (user_id: number, user_name: string) => {
    if (user_id && user_name && user_name !== 'Campaign') {
      return (
        <>
          <span className='ml-[2px] mr-1'>by</span>
          <UserPopover userName={user_name} userId={user_id} />
        </>
      )
    }
    return null
  }

  const { command } = useTaskAwareMetadata()

  // Convert string command to CommandType enum
  const commandType = command ? commandTypeFromJSON(command) : undefined

  const isDocCampaignCompleted =
    isCampaign &&
    isSuccessTerminated(taskStatus) &&
    commandType === CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION
  const renderDesc = (desc: string) => {
    if (isDocCampaignCompleted) {
      return '✅ Task Completed'
    }

    // Don't show user info for migration endpoints selected state
    const shouldShowUser = taskStatus !== AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED

    return (
      <>
        <div>
          {desc} {shouldShowUser ? renderUser(user_id, user_name) : null}
        </div>
      </>
    )
  }

  const isLastBlock = () => {
    return index == totalBlock - 1
  }

  // Determine if this block should show the active spinner
  const isActiveBlock = useMemo(() => {
    if (!isLastBlock()) return false
    if (!currentTaskStatus) return false
    const group = TaskStatuses[currentTaskStatus]?.group
    return group === 'IN_PROGRESS' || group === 'NEED_ACTION'
  }, [currentTaskStatus, index, totalBlock])

  // Memoize diffs extraction and categorization
  const { diffs, imageDiffs, docDiffs } = useMemo(() => {
    if (!currentTuple) return { diffs: [], imageDiffs: [], docDiffs: [] }
    const allDiffs = currentTuple.response?.[0]?.responseContentStream?.diff || []

    const images = allDiffs.filter(diff => {
      const filePath = diff.bFilePath || diff.aFilePath || ''
      return (
        filePath.endsWith('.png.b64') ||
        filePath.endsWith('.jpg.b64') ||
        filePath.endsWith('.jpeg.b64') ||
        filePath.endsWith('.svg.b64')
      )
    })

    const docs = allDiffs.filter(diff => {
      const filePath = diff.bFilePath || diff.aFilePath || ''
      return !(
        filePath.endsWith('.png.b64') ||
        filePath.endsWith('.jpg.b64') ||
        filePath.endsWith('.jpeg.b64') ||
        filePath.endsWith('.svg.b64')
      )
    })

    return { diffs: allDiffs, imageDiffs: images, docDiffs: docs }
  }, [currentTuple])

  const hasDiffs = diffs.length > 0

  const [imageFeedback, setImageFeedback] = useState('')
  const [isImageFeedbackDisabled, setIsImageFeedbackDisabled] = useState(false)

  return (
    <div className={cm('flex relative', styles.timeline)}>
      <TimelineStatusIcon isActive={isActiveBlock} />
      <div
        className={cm(
          'timeline-line',
          'absolute top-[9px] left-[15px] bottom-[-5px] w-[1px] text-primary-50 opacity-70',
          styles.line,
          isActiveBlock && styles.lineWithSpinner
        )}
      ></div>
      <div className='timeline-body pl-8 pt-2 pb-5 max-w-xl'>
        <div className='date text-xs text-default-500 pb-2'>{renderDate(ts)}</div>
        <div className='desc text-default-500 flex flex-col justify-center'>
          <div>{renderDesc(desc)}</div>
          {hasTaskDetail &&
          !isDocCampaignCompleted &&
          !hasPlan &&
          !documentGenerated &&
          taskStatus !== AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED &&
          taskStatus !== AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED &&
          taskStatus !== AgentTaskStatus.REFACTOR_API_SPECS_GENERATED ? (
            <div className={cm('text-default-500 text-xs ml-6 mt-2 underline', 'text-[#1b213f99]')}>
              <span
                className='cursor-pointer  hover:text-[#1b213f66] select-none'
                onClick={() => onClickDetails(taskStatus)}
              >
                See details
              </span>
            </div>
          ) : null}

          {/* See docs and diagrams for REFACTOR_API_SPECS_GENERATED when current or past */}
          {hasTaskDetail &&
          !isDocCampaignCompleted &&
          !hasPlan &&
          !documentGenerated &&
          taskStatus === AgentTaskStatus.REFACTOR_API_SPECS_GENERATED ? (
            <div className='ml-6 mt-3 flex gap-2'>
              {docDiffs.length > 0 && (
                <Button variant='outlined' onClick={onClickDocs} className='text-default-500'>
                  See docs
                </Button>
              )}
              {imageDiffs.length > 0 && (
                <Button variant='outlined' onClick={onClickDiagram} className='text-default-500'>
                  See diagram
                </Button>
              )}
            </div>
          ) : null}
          {internalMessage ? (
            <div className='text-sm mt-1 ml-6 max-w-[800px] break-words whitespace-pre-wrap'>
              {isLastBlock() ? '' : ''}
              {internalMessage}
            </div>
          ) : null}
          {hasTaskDetail &&
          !isDocCampaignCompleted &&
          !hasPlan &&
          !documentGenerated &&
          taskStatus === AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED &&
          currentTaskStatus !== AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED ? (
            <div className={cm('text-default-500 text-xs ml-6 mt-2 underline', 'text-[#1b213f99]')}>
              <span
                className='cursor-pointer  hover:text-[#1b213f66] select-none'
                onClick={() => onClickDetails(taskStatus)}
              >
                See details
              </span>
            </div>
          ) : null}
          {hasTaskDetail &&
          !isDocCampaignCompleted &&
          !hasPlan &&
          !documentGenerated &&
          taskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED &&
          isLastBlock() &&
          currentTaskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED ? (
            <div className='ml-6 mt-3'>
              <Button
                variant='outlined'
                onClick={() => onClickDetails(taskStatus)}
                className='text-default-500'
                rightIcon={
                  <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5l7 7-7 7'
                    />
                  </svg>
                }
              >
                Select Endpoint(s)
              </Button>
            </div>
          ) : null}
          {hasTaskDetail &&
          !isDocCampaignCompleted &&
          !hasPlan &&
          !documentGenerated &&
          taskStatus === AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED &&
          currentTaskStatus === AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED ? (
            <div className='ml-6 mt-3'>
              <Button
                variant='outlined'
                onClick={() => onClickDetails(taskStatus)}
                className='text-default-500'
                rightIcon={
                  <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5l7 7-7 7'
                    />
                  </svg>
                }
              >
                Continue with Refactoring
              </Button>
            </div>
          ) : null}
        </div>
        {hasPlan ? <Planner readOnly={!isLastBlock()} /> : null}
        {documentGenerated ? <Document index={index} _uuid={uuid} /> : null}
        <div className='children space-y-4'>
          {children.map(block => (
            <Block
              key={block.id}
              block={block}
              className={cm(`block-${block.id}`, 'pb-8')}
              childrenClassName='pl-0'
              _uuid={uuid}
            />
          ))}
        </div>
        {!isAdminTask && imageGenerated && isLastBlock() && !isImageFeedbackDisabled ? (
          <div className='flex justify-between gap-x-2'>
            <Input
              value={imageFeedback}
              onChange={e => setImageFeedback(e.target.value)}
              placeholder='Provide feedback for the image'
            />
            <Button
              variant='primary'
              onClick={async () => {
                if (!task?.task_id || !workspaceUuid || !uuid) return
                setIsImageFeedbackDisabled(true)
                await regenerateImage({
                  workspaceUuid,
                  uuid,
                  taskId: task?.task_id,
                  comment: imageFeedback
                })
              }}
              isDisabled={!task?.task_id || !workspaceUuid || !uuid}
            >
              Regenerate
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
